<template>
  <v-dialog
    v-model="localShow"
    max-width="600"
    origin="center center"
    transition="dialog-transition"
    value="value"
    persistent
  >
    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1">Wybierz usługę</v-stepper-step>

        <v-divider />

        <v-stepper-step step="2">Wybrana usługa</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card class="mb-5" elevation="0">
            <v-card-text>
              <v-list two-line>
                <v-list-tile
                  v-for="(benefit, index) in benefits"
                  :key="index"
                  avatar
                  :class="{
                    'grey lighten-2': benefit.notEnoughPoints
                  }"
                  @click="benefit.notEnoughPoints ? undefined : select(benefit)"
                >
                  <v-list-tile-avatar>
                    <v-icon color="primary" medium size="example">{{ benefit.type | icon }}</v-icon>
                  </v-list-tile-avatar>

                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ benefit.name }}

                      <v-tooltip v-if="benefit.containerBenefit && benefit.containerBenefit.length" top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on">mdi-shopping</v-icon>
                        </template>

                        Użycie usługi naliczy punkty:
                        <div
                          v-for="(containerBenefit, index) in benefit.containerBenefit"
                          :key="'containerBenefit_' + index"
                        >
                          {{ containerBenefit.pointContainer.name + ': ' + containerBenefit.points + ' pkt.' }}
                        </div>
                      </v-tooltip>

                      <v-tooltip v-if="benefit.shared" top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on">mdi-share-variant</v-icon>
                        </template>

                        <span>Usługa współdzielona</span>
                      </v-tooltip>
                    </v-list-tile-title>

                    <v-list-tile-sub-title>{{ benefit.value }} {{ benefit.type | type }}</v-list-tile-sub-title>

                    <v-list-tile-sub-title v-if="benefit.notEnoughPoints" class="red--text">
                      Brak wymaganej ilości punktów: {{ benefit.userPoints || 0 }} / {{ benefit.pointCost }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <v-list-tile-action>
                    <v-btn flat color="blue darken-3" :disabled="benefit.notEnoughPoints" @click="select(benefit)">
                      Wybierz
                    </v-btn>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </v-card-text>
          </v-card>

          <v-divider />

          <v-layout row wrap>
            <v-btn color="red darken-3" flat @click="close">Anuluj</v-btn>
          </v-layout>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-card class="mb-5" elevation="0">
            <v-card-text>
              <v-layout mx-4 row wrap>
                <v-flex xs12>
                  <v-text-field
                    label="Wybrana usługa"
                    :error-messages="$schema.benefitId.$error"
                    :value="selectedName()"
                    :readonly="true"
                  >
                    <template v-slot:append-outer>
                      <v-btn bottom large flat color="success" @click="step = 1">Zmień</v-btn>
                    </template>
                  </v-text-field>
                </v-flex>

                <v-spacer />

                <v-flex xs12>
                  <w-schema :model="$schema.price">
                    <v-text-field name="price" label="Cena bazowa (bez zniżki)" />
                  </w-schema>
                </v-flex>

                <v-flex v-if="valueDisplay" xs12>
                  <w-schema :model="$schema.value">
                    <v-text-field name="value" :label="valueLabel" />
                  </w-schema>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

          <v-divider />

          <v-layout row wrap>
            <v-btn color="red darken-3" flat @click="close">Anuluj</v-btn>

            <v-spacer />

            <v-btn color="success" @click="use">Użyj</v-btn>
          </v-layout>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-dialog>
</template>

<script>
import {
  BenefitType,
  BenefitTypeTranslation,
  DisplayBenefitType,
  DisplayBenefitTypeTranslation,
  formatError,
  SessionStorage
} from '@witkac/kartamiejska-shared';
import { mapState } from 'vuex';

import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/benefitUsage.schema';

const emptyData = () => ({
  price: null,
  value: null,
  benefitId: null,
  cardNumber: '',
  placeId: parseInt(localStorage.getItem(SessionStorage.PLACE_ID))
});

// @vuese
// Modal użycia karty u partnera
// @group cardUsage
export default {
  filters: {
    icon(val) {
      return DisplayBenefitTypeTranslation[BenefitType[val]];
    },
    type(val) {
      return BenefitTypeTranslation[BenefitType[val]];
    }
  },
  mixins: [formMixin, notificationsMixin],
  schema,
  props: {
    // Kontroluje widoczność modala
    show: {
      type: Boolean,
      required: true
    },
    // Numer karty która wywołała modal
    cardnumber: {
      type: String
    }
  },
  data() {
    return {
      localShow: false,
      selected: undefined,
      model: {},
      valueLabel: '',
      valueDisplay: false,
      step: 1
    };
  },
  computed: {
    ...mapState({
      benefits: state =>
        state.partners.benefits.available.list
          .map(benefit => ({
            ...benefit,
            notEnoughPoints: benefit.pointCost && benefit.pointCost > (benefit.userPoints || 0)
          }))
          .sort(a => (a.notEnoughPoints ? 1 : -1))
    })
  },
  watch: {
    show(val) {
      this.localShow = val;
    },
    cardnumber(val) {
      this.$schema.$model.cardNumber = val;
    }
  },

  beforeMount() {
    this.clearData();
  },
  methods: {
    // @vuese
    // Funkcja emituje `close` które zamyka modal
    close() {
      this.clearData();
      // @vuese
      // zamknięcie modala
      this.$emit('close');
    },
    // @vuese
    // Wybranie usługi do użycia
    // @arg id - ID usługi do użycia
    // @arg value - Wartość usługi
    select(benefit) {
      const { id, value, type } = benefit;
      this.$schema.$model.benefitId = id;
      this.$schema.benefitId.$validate();
      this.selected = id;
      if (type === DisplayBenefitType.TIME || type === DisplayBenefitType.USAGES) {
        this.valueDisplay = true;
        this.valueLabel = type === DisplayBenefitType.TIME ? 'Ilość czasu' : 'Liczba wejść';
      } else {
        this.valueDisplay = false;
      }
      this.step = 2;
    },
    // @vuese
    // Nazwa do wyświetlenia koło kwoty
    selectedName() {
      if (!this.selected) {
        return '';
      }
      const selected = this.benefits.find(item => item.id === this.selected);
      this.$schema.price.$setData(selected.defaultPrice);
      return selected ? selected.name : '';
    },
    // @vuese
    // Użycie usługi
    use() {
      const errors = this.$schema.$validateAll();
      if (errors.length === 0) {
        this.$store
          .dispatch('benefitUse/add', this.$schema.$model)
          .then(() => {
            this.notifySuccess('Wykorzystano zniżkę.');
            const partnerId = parseInt(localStorage.getItem(SessionStorage.PARTNER_ID));
            this.$store.dispatch('stats/getCardUsages', partnerId);
            this.$store.dispatch('stats/getBenefitsValue', partnerId);
            this.close();
          })
          .catch(error => this.notifyError(formatError(error)));
      } else {
        this.notifyError('Niepoprawnie wypełniony formularz.');
      }
    },
    // @vuese
    // Czyszczenie formularza
    clearData() {
      this.step = 1;
      this.$schema.$setData(emptyData());
      this.$schema.$reset();
      this.selected = undefined;
    }
  }
};
</script>
